/**
 * Canonical medication occurrence expansion (#2223).
 *
 * A scheduled medication runs every day from its start date through its end
 * date, INCLUSIVE on both ends ("Once" = endDate === startDate — see
 * AddMedicationModal). Every surface that renders occurrences (legacy
 * calendar items, the concept calendar/Up Next markers, Cycle Detail) must
 * expand dates through this module so counts and ranges agree everywhere.
 */

export interface MedicationOccurrenceWindow {
  startDate?: string | null;
  endDate?: string | null;
}

function isoDateOnly(value: string | null | undefined): string | null {
  if (!value) return null;
  const match = /^(\d{4}-\d{2}-\d{2})/.exec(value);
  return match ? match[1]! : null;
}

function addDays(iso: string, days: number): string {
  // All arithmetic in UTC: parsing as local midnight but serializing via
  // toISOString() (UTC) makes the date fail to advance in UTC+ timezones,
  // which spins the expansion loop forever and freezes the app.
  const date = new Date(`${iso}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

function compareIsoDates(a: string, b: string) {
  return a.localeCompare(b);
}

/**
 * Expand a medication's schedule into the ISO dates it occurs on, clamped to
 * the requested range. An open-ended medication runs to the range end. With
 * neither a range nor a medication end, only the start date is emitted (an
 * unbounded expansion would be infinite).
 *
 * A medication with no parseable start date used to anchor to the range
 * start, which painted a dose on every day of whatever window was asked
 * for. That was invisible while the calendar could only reach a year either
 * side of today; now that it reaches a patient's whole history, it would
 * claim she was taking the drug in months that predate the record. We do
 * not know when such a course began, so it contributes no occurrences.
 * `PatientMedication.startDate` is a required string, so this guards a
 * malformed value rather than an ordinary one.
 */
export function expandMedicationOccurrenceDates(
  med: MedicationOccurrenceWindow,
  range: MedicationOccurrenceWindow = {},
): string[] {
  const rangeStart = isoDateOnly(range.startDate) ?? isoDateOnly(med.startDate);
  const rangeEnd = isoDateOnly(range.endDate) ?? isoDateOnly(med.endDate);
  const medStart = isoDateOnly(med.startDate);
  const medEnd = isoDateOnly(med.endDate) ?? rangeEnd;

  if (!medStart) return [];
  if (!rangeStart || !rangeEnd || !medEnd) {
    return [medStart];
  }

  let current =
    compareIsoDates(medStart, rangeStart) > 0 ? medStart : rangeStart;
  const end = compareIsoDates(medEnd, rangeEnd) < 0 ? medEnd : rangeEnd;
  if (compareIsoDates(current, end) > 0) return [];

  const dates: string[] = [];
  while (compareIsoDates(current, end) <= 0) {
    dates.push(current);
    current = addDays(current, 1);
  }
  return dates;
}

/**
 * True when the medication's course overlaps the range at all — the filter
 * surfaces must use before expanding (a medication that started before the
 * window but is still running belongs in the window).
 */
export function medicationOverlapsRange(
  med: MedicationOccurrenceWindow,
  range: MedicationOccurrenceWindow,
): boolean {
  const medStart = isoDateOnly(med.startDate);
  const medEnd = isoDateOnly(med.endDate);
  const rangeStart = isoDateOnly(range.startDate);
  const rangeEnd = isoDateOnly(range.endDate);
  if (rangeEnd && medStart && compareIsoDates(medStart, rangeEnd) > 0) {
    return false;
  }
  if (rangeStart && medEnd && compareIsoDates(medEnd, rangeStart) < 0) {
    return false;
  }
  return true;
}
